import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { authSelectors } from 'app-components/flux/auth';
import { entitlementsActions, entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { subscriptionsActions, subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { userInfoSelectors } from 'app-components/flux/user-info';
import { useFetchOnce } from 'app-components/hooks/utils';
import { getBuildConfig, getLogger, intercomTracker, tracker } from 'app-components/utils/core';

import { useGlobalCobrowseRequired } from 'utils/cobrowse';

const log = getLogger('utils/intercom.js');
const APP_ID = getBuildConfig().intercom_app_id;

const Intercom = (_props) => {
  const intercomEnabled = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'intercom'));
  const profile = useSelector(profileSelectors.getProfile);
  const authSession = useSelector(authSelectors.getAuthSession);
  const userInfo = useSelector(userInfoSelectors.getUserInfo);
  const activeSubscription = useSelector(subscriptionsSelectors.getActiveSubscription);
  const datasetEntitlement = useSelector(entitlementsSelectors.getTopTierEntitlement);
  const [, setCobrowseRequired] = useGlobalCobrowseRequired();
  useFetchOnce(profileActions.getProfile, profileSelectors, false, true);
  useFetchOnce(subscriptionsActions.getSubscriptions, subscriptionsSelectors, false, true);
  useFetchOnce(entitlementsActions.getEntitlements, entitlementsSelectors, false, true);

  useEffect(() => {
    if (intercomEnabled) {
      /* eslint-disable */
      if (APP_ID) {
        //Set your APP_ID
        try {
          (function () {
            var w = window;
            var ic = w.Intercom;
            if (typeof ic === 'function') {
              ic('reattach_activator');
              ic('update', w.intercomSettings);
            } else {
              var d = document;
              var i = function () {
                i.c(arguments);
              };
              i.q = [];
              i.c = function (args) {
                i.q.push(args);
              };
              w.Intercom = i;
              var l = function () {
                var s = d.createElement('script');
                s.type = 'text/javascript';
                s.async = true;
                s.src = 'https://widget.intercom.io/widget/' + APP_ID;
                var x = d.getElementsByTagName('script')[0];
                x.parentNode.insertBefore(s, x);
              };
              if (document.readyState === 'complete') {
                l();
              } else if (w.attachEvent) {
                w.attachEvent('onload', l);
              } else {
                w.addEventListener('load', l, false);
              }
            }
          })();

          window.intercomSettings = {
            app_id: APP_ID,
            custom_launcher_selector: '#intercom-launcher',
            hide_default_launcher: true,
          };
          updateIntercom();
        } catch (error) {
          assert(false, error);
        }
      }
    } else {
      try {
        window.Intercom?.('shutdown');
      } catch (error) {
        assert(false, error);
      }
    }
  }, [intercomEnabled]);

  const updateIntercom = useCallback(() => {
    try {
      if (window.Intercom) {
        const name = [
          ...(profile?.firstName ? [profile.firstName] : []),
          ...(profile?.lastName ? [profile.lastName] : []),
        ].join(' ');
        window.Intercom('boot');
        window.Intercom('onShow', () => {
          tracker.track(tracker.events.showIntercom);
          // enable co-browse
          setCobrowseRequired(true);
        });
        const metaData = {
          user_id: authSession?.qcsId,
          qcs_id: authSession?.qcsId,
          dataset_id: authSession?.datasetId,
          scope: authSession?.scope,
          groups: authSession?.groups,
          source: userInfo?.source,
          email: profile?.username,
          name,
          subscription_tier: activeSubscription?.tierUriName,
          dataset_tier: datasetEntitlement?.id,
        };
        log.debug('\nintercom identify:\n', metaData);
        window.Intercom('update', metaData);

        intercomTracker.registerEventDispatcher((eventName, meta) => window.Intercom('trackEvent', eventName, meta));
      }
    } catch (e) {
      assert(false, e);
    }

    return () => window.Intercom?.('shutdown');
  }, [authSession, profile, userInfo]);

  useEffect(() => {
    if (intercomEnabled) {
      updateIntercom();
    }
  }, [profile, authSession, updateIntercom, intercomEnabled]);

  return null;
};

export default React.memo(Intercom);
